
import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'JobPosting',
  '@id': `${siteUrl}/recruit`,
  title: '電気工事士',
  description: '民間・公共工事の電気設備工事に関する施工（施工先：住宅・店舗・工場・ビル）、配線工事、照明器具の取り付けなど',
  hiringOrganization: {
    '@type': 'Organization',
    name: '株式会社鈴電エンジニア',
    sameAs: siteUrl,
  },
  jobLocation: {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '伏黒字上ケ戸71-6',
      addressLocality: '伊達市',
      addressRegion: '福島県',
      postalCode: '960-0501',
      addressCountry: 'JP',
    },
  },
  employmentType: 'FULL_TIME',
  workHours: '8:00〜17:00',
};

export default function RecruitPage() {
  const heroAnimation = useScrollAnimation({ threshold: 0.2 });
  const messageAnimation = useScrollAnimation({ threshold: 0.2 });
  const positionsAnimation = useScrollAnimation({ threshold: 0.2 });
  const applicationAnimation = useScrollAnimation({ threshold: 0.2 });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [
    'https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/3a63255ff6d5aa8dacaf636b4e6ab065.jpeg',
    'https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/54b8638e00f972697003c9d2c541cd5b.jpeg'
  ];

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO meta
    document.title = '採用情報 | 株式会社鈴電エンジニア';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアでは電気工事士を募集しています。未経験者歓迎。福島県伊達市の設備工事会社で一緒に働きませんか。');

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-recruit';
    script.text = JSON.stringify(schemaJsonLd);
    document.head.appendChild(script);

    return () => {
      document.title = '株式会社鈴電エンジニア';
      if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアは、電気設備工事を中心に、空調・消防設備工事など、建物に関わる各種設備工事を行っています。');
      const el = document.getElementById('schema-recruit');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const handleContactNavigate = () => {
    window.REACT_APP_NAVIGATE('/contact');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 hidden md:flex animate-hero-zoom">
          <div className="relative w-1/2 h-full">
            <img src={heroImages[0]} alt="採用情報 左" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-y-0 right-0 w-1/4 bg-gradient-to-r from-transparent to-black/0" style={{
              maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
              WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)'
            }}></div>
          </div>
          <div className="relative w-1/2 h-full">
            <img src={heroImages[1]} alt="採用情報 右" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-l from-transparent to-black/0" style={{
              maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
              WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)'
            }}></div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="absolute inset-0 md:hidden animate-hero-zoom">
          {heroImages.map((image, index) => (
            <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}>
              <img src={image} alt={"採用情報 " + (index + 1)} className="w-full h-full object-cover object-center" />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div 
          ref={heroAnimation.ref as React.RefObject<HTMLDivElement>}
          className={`relative z-10 text-center text-white px-6 scroll-animate ${heroAnimation.isVisible ? 'is-visible' : ''}`}
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">採用情報</h1>
        </div>
      </section>

      {/* Message */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div ref={messageAnimation.ref as React.RefObject<HTMLDivElement>} className={`text-center scroll-animate ${messageAnimation.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-gray-900">共に成長する仲間を募集中</h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-5 sm:mb-6 text-left sm:text-center">
              確かな技術を身につけ、自分に自信を持てる仕事をしませんか。
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 text-left sm:text-center">
              当社では、年齢や経験に関係なく、分からないことは丁寧に教え合い、<br className="hidden sm:inline" />
              一人で抱え込ませない職場づくりを大切にしています。
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-left sm:text-center">
              経験者の方はもちろん、未経験の方も大歓迎です。<br className="hidden sm:inline" />
              あなたの挑戦を会社全体でしっかりと支えていきます。
            </p>
          </div>
        </div>
      </section>

      {/* Positions */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div ref={positionsAnimation.ref as React.RefObject<HTMLDivElement>} className={`scroll-animate ${positionsAnimation.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">募集要項</h2>
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-blue-400">
                <div className="flex items-start">
                  <div className="flex-1 p-8">
                    <h3 className="text-2xl font-bold mb-6 text-gray-900 border-b-2 border-gray-200 pb-3">電気工事士</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">職種</div>
                        <div className="text-gray-600">電気施工管理<br />電気工事作業員（経験不問）</div>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">仕事内容</div>
                        <div className="text-gray-600">民間・公共工事の電気設備工事に関する施工（施工先：住宅・店舗・工場・ビル）、<br />配線工事、照明器具の取り付けなど</div>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">学歴</div>
                        <div className="text-gray-600">不問</div>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">必要な免許<br />・資格</div>
                        <div className="text-gray-600">普通自動車免許<br />第2種電気工事士あれば尚可</div>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">勤務時間</div>
                        <div className="text-gray-600">8:00～17:00</div>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">休憩</div>
                        <div className="text-gray-600">90分</div>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">休日</div>
                        <div className="text-gray-600">日、祝日、週休2日制、年末年始、お盆休み、その他</div>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
                        <div className="font-semibold text-gray-700">採用方法</div>
                        <div className="text-gray-600">面接・書類選考</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Method */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div ref={applicationAnimation.ref as React.RefObject<HTMLDivElement>} className={`text-center scroll-animate ${applicationAnimation.isVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl font-bold mb-8 text-gray-900">応募方法</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              何かご不明な点がございましたら、電話またはメールよりお気軽にご連絡ください。<br />
              皆様のご応募を心よりお待ちしております。
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mt-10">
              <div className="flex items-center gap-3 text-gray-800">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-600 mb-1">お電話でのお問い合わせ</p>
                  <p className="font-bold text-xl text-blue-600">024-583-4612</p>
                  <p className="text-xs text-gray-500">&nbsp;8:00～17:00（土日・祝日を除く）</p>
                </div>
              </div>
              <button
                onClick={handleContactNavigate}
                className="group inline-flex items-center gap-3 px-8 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
              >
                <i className="ri-mail-line text-xl"></i>
                <span className="font-semibold">メールでお問い合わせ</span>
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
